
import React from 'react';
import { getFeaturedArticles, featuredAchievers } from '@/data/articles';
import { Clock, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CategoryBadge from '@/components/ui/CategoryBadge';
import { successStories } from '@/data/successStories';
import { Link } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useIsMobile } from '@/hooks/use-mobile';

const FeaturedSection: React.FC = () => {
  const featuredArticles = getFeaturedArticles();
  const isMobile = useIsMobile();

  // Get the first success story with proper null checking
  const mainStory = successStories && successStories.length > 0 ? successStories[0] : null;
  if (!mainStory) return null;

  // Get the linked achiever details with null checking
  const linkedAchiever = mainStory.achieverId ? featuredAchievers.find(achiever => achiever.id === mainStory.achieverId) : null;

  // Use achiever data when available, fallback to story data
  const displayName = linkedAchiever ? linkedAchiever.author.name : mainStory.name;
  const displayImage = linkedAchiever ? linkedAchiever.author.avatar : mainStory.image; // Use avatar for profile
  const displayRole = linkedAchiever ? linkedAchiever.title : mainStory.profileTitle || mainStory.stats?.impact || "Achiever";

  // Determine thumbnail image to use with proper null checking (prioritize order: 1. story thumbnail, 2. achiever imageUrl, 3. story image)
  const thumbnailImage = mainStory.thumbnail || (linkedAchiever ? linkedAchiever.imageUrl : null) || mainStory.image || '/placeholder.svg';

  // Get second success story with proper null checking
  const secondStory = successStories && successStories.length > 1 ? successStories[1] : null;

  return (
    <section className="pt-4 md:pt-10 pb-4 md:pb-8 px-4 md:px-6 w-full">
      <div className="container mx-auto w-full max-w-[1280px] flex justify-center">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          {/* Main Featured Story - Takes 2/3 width on medium screens */}
          <div className="md:col-span-2">
            <div className="relative overflow-hidden rounded-xl h-[350px] md:h-[450px]">
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent z-10" />

              <div className="relative w-full h-full">
                <img
                  src={thumbnailImage}
                  alt={mainStory.title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={e => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/placeholder.svg';
                  }}
                />
              </div>

              <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 md:p-8">
                <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2 md:mb-3">
                  <div className="bg-amber-500/90 backdrop-blur-sm text-white px-2 md:px-3 py-1 rounded-full text-xs font-semibold">
                    ₹1 CRORE ANNUAL TURNOVER
                  </div>
                  {/* <div className="bg-purple-500/90 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-semibold">
                    25L MONTHLY
                  </div> */}
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 md:mb-4 leading-tight">
                  {/* From Software to Spice */}
                  {/* From Government Job to Crore Club */}
                  Tears Turned to 1Cr Turnover: journey of not just Survival but of Scale.
                </h1>

                <p className="text-white/90 mb-4 md:mb-5 text-sm md:text-base line-clamp-3 md:line-clamp-none">
                  {/* Meet Sahithi Chiluveru, a tech professional-turned-entrepreneur who has transformed her passion for traditional Telugu flavors into a ₹1 crore business, empowering women along the way. As the founder of Flavours of Telugu, she has taken the humble art of pickle-making to new heights. */}
                  {/* Building a ₹3 crore beverage empire with Desi Chai & BBC Café */}
                  Building ₹1 crore wholesale clothing business against all odds
                </p>

                <div className="flex flex-col xs:flex-row items-start xs:items-center gap-3 md:gap-4">
                  <div className="flex items-center gap-2 md:gap-3 bg-black/30 backdrop-blur-sm px-3 md:px-4 py-1.5 md:py-2 rounded-lg">
                    <Avatar className="h-8 w-8 md:h-10 md:w-10 border-2 border-white/40">
                      <AvatarImage src={displayImage} alt={displayName} className="object-cover" />
                      <AvatarFallback>{displayName?.charAt(0) || 'A'}</AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-xs md:text-sm font-medium text-white">{displayName}</span>
                      <span className="text-xs text-white/70">
                        {/* Founder of Desi Chai & BBC Café */}
                        Founder of Amma Selection
                      </span>
                    </div>
                  </div>

                  <Button variant="outline" size={isMobile ? "sm" : "default"} className="bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20 text-xs md:text-sm" asChild>
                    <Link to={`/success-story/${mainStory.id}`}>
                      Read Full Story <ExternalLink className="ml-1 md:ml-2 h-3 w-3 md:h-4 md:w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>


          {/*With Lables in the top left corner*/}
          {secondStory && <div className="md:col-span-1">
            <div className="relative overflow-hidden rounded-xl h-[350px] md:h-[450px]">
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent z-10" />

              <div className="relative w-full h-full">
                <img
                  src={secondStory.thumbnail || secondStory.image || '/placeholder.svg'}
                  alt={secondStory.title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={e => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/placeholder.svg';
                  }}
                />
              </div>

              {/* Moved labels container to top left */}
              <div className="absolute top-4 left-4 z-20 flex flex-wrap items-center gap-2">
                {secondStory.stats && secondStory.stats.revenue && (
                  <div className="bg-amber-500/90 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-semibold">
                    {secondStory.stats.revenue.toUpperCase()}
                  </div>
                )}
                {secondStory.stats && secondStory.stats.impact && (
                  <div className="bg-purple-500/90 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-semibold">
                    {secondStory.stats.impact.toUpperCase()}
                  </div>
                )}
              </div>

              {/* Rest of the content remains at bottom */}
              <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight">
                  {secondStory.title}
                </h2>

                <p className="text-white/90 mb-3 text-sm line-clamp-3">
                  {secondStory.oneLiner}
                </p>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20"
                  asChild
                >
                  <Link to={`/success-story/${secondStory.id}`}>
                    Read Full Story <ExternalLink className="ml-2 h-3 w-3" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>}

        </div>
      </div>

      <div className="container mx-auto w-full max-w-[1280px] flex justify-center mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {/* Grid stories - starting from index 2 */}
          {successStories && successStories.slice(2).map((story) => story && (
            <div key={story.id} className="relative overflow-hidden rounded-xl h-[250px]">
              {/* Gradient overlay - focus on bottom third */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent z-10" />

              {/* Story image */}
              <div className="relative w-full h-full">
                <img
                  src={story.thumbnail || story.image || '/placeholder.svg'}
                  alt={story.title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={e => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/placeholder.svg';
                  }}
                />
              </div>

              {/* NEW CODE START - Content container */}
              <div className="absolute inset-0 z-20 flex flex-col justify-between p-5 pt-4">
                {/* Top section - badges */}
                {story.stats && Object.keys(story.stats).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {story.stats.revenue && (
                      <div className="bg-amber-500/90 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-semibold">
                        {story.stats.revenue.toUpperCase()}
                      </div>
                    )}
                    {story.stats.impact && (
                      <div className="bg-purple-500/90 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-semibold">
                        {story.stats.impact.toUpperCase()}
                      </div>
                    )}
                    {story.stats.employeeCount && (
                      <div className="bg-blue-500/90 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-semibold">
                        {story.stats.employeeCount.toUpperCase()}
                      </div>
                    )}
                    {story.stats.partners && (
                      <div className="bg-green-500/90 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-semibold">
                        {story.stats.partners.toUpperCase()}
                      </div>
                    )}
                  </div>
                )}

                {/* Bottom section - text content */}
                <div className="mt-auto space-y-2">
                  <h3 className="text-lg md:text-xl font-bold text-white leading-tight">
                    {story.title}
                  </h3>

                  <p className="text-white/90 text-sm line-clamp-2">
                    {story.oneLiner}
                  </p>

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20"
                    asChild
                  >
                    <Link to={`/success-story/${story.id}`}>
                      Read Story
                    </Link>
                  </Button>
                </div>
              </div>
              {/* NEW CODE END */}
            </div>
          ))}
        </div>
      </div>


    </section>
  );
};

export default FeaturedSection;
